import { authConfig } from '@/features/auth/config'
import { getDeviceInstallationId } from '@/shared/browser/device-installation-id'

/**
 * 后端 API 基础地址（与 auth API 共用同一个后端）。
 */
export const appApiBaseUrl = authConfig.apiBaseUrl

/**
 * App 层 API 请求封装。
 * 与 auth-service.ts 的 requestJson 一致：credentials: 'include'，JSON body。
 */
export class AppApiError extends Error {
  constructor(
    message: string,
    public statusCode: number,
  ) {
    super(message)
    this.name = 'AppApiError'
  }
}

export async function appFetch<T>(
  path: string,
  init?: RequestInit,
): Promise<T> {
  const url = path.startsWith('http')
    ? path
    : `${appApiBaseUrl}${path}`

  const response = await fetch(url, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(getDeviceInstallationId() ? { 'X-FastCut-Device-Id': getDeviceInstallationId()! } : {}),
      ...(init?.headers ?? {}),
    },
  })

  const payload = (await response.json().catch(() => null)) as
    | T
    | { ok: false; message?: string }
    | null

  if (!response.ok) {
    const message =
      payload && typeof payload === 'object' && 'message' in payload
        ? String(payload.message)
        : `请求失败 (${response.status})`
    throw new AppApiError(message, response.status)
  }

  if (payload === null) {
    throw new AppApiError('接口未返回 JSON 数据', response.status)
  }

  return payload as T
}

/**
 * 文件上传（multipart/form-data），用 XHR 支持上传进度回调。
 */
export function uploadFile<T>(
  path: string,
  file: File,
  onProgress?: (percent: number) => void,
  fields?: Record<string, string>,
): Promise<T> {
  const url = path.startsWith('http')
    ? path
    : `${appApiBaseUrl}${path}`

  const formData = new FormData()
  Object.entries(fields ?? {}).forEach(([key, value]) => formData.append(key, value))
  formData.append('file', file)

  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', url)
    xhr.withCredentials = true
    const deviceInstallationId = getDeviceInstallationId()
    if (deviceInstallationId) xhr.setRequestHeader('X-FastCut-Device-Id', deviceInstallationId)

    if (onProgress) {
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100))
        }
      })
    }

    xhr.addEventListener('load', () => {
      let payload: T | { ok: false; message?: string } | null
      try {
        payload = JSON.parse(xhr.responseText) as T | { ok: false; message?: string }
      } catch {
        payload = null
      }

      if (xhr.status < 200 || xhr.status >= 300) {
        const message =
          payload && typeof payload === 'object' && 'message' in payload
            ? String(payload.message)
            : `上传失败 (${xhr.status})`
        reject(new AppApiError(message, xhr.status))
        return
      }

      if (payload === null) {
        reject(new AppApiError('接口未返回 JSON 数据', xhr.status))
        return
      }

      resolve(payload as T)
    })

    xhr.addEventListener('error', () => {
      reject(new AppApiError('网络错误，上传失败', 0))
    })

    xhr.send(formData)
  })
}
